import React, { useState } from "react";
import { toast } from "sonner";
import { Warning, Sparkle, CaretDown, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";

// The exact cascade order the backend uses in FILL_GAP_CASCADE
// (see routes/optimize.py). Kept in sync manually — order matters
// because each level is the smallest meaningful loosening of ONE
// knob. Users see EXACTLY the same sequence the solver walks so
// there are no surprises about what got relaxed.
const CASCADE_STEPS = [
  { title: "Reduce salary floor by $500",         detail: "First — a tiny $500 salary-floor concession opens up any near-optimum lineup that was blocked by 3-4 cents of cap." },
  { title: "Widen ownership sum window ±5%",      detail: "Next — expand the total-ownership band by 5 points either way so lineups with slightly chalkier or slightly more contrarian ownership become feasible." },
  { title: "Reduce salary floor by $1,500 total", detail: "If still short — deeper salary-floor relief (still under 3% of cap) to unlock value-plus-value builds." },
  { title: "Drop low-owned pivot minimum to 0",  detail: "Next — allow lineups without the 2-pivot minimum. Existing lineups still have their pivots; only new lineups get this pass." },
  { title: "Widen ownership sum window ±15%",     detail: "Bigger ownership window — usually only reached on very tight custom slates. Cluster Rotation still dedupes." },
  { title: "Reduce salary floor by $3,000 total", detail: "Last resort — the deepest floor drop. If we're still short after this, the pool is genuinely too tight to build the requested count without breaking Combat Stack Matrix." },
];

export default function ShortfallBanner({
  requested,
  count,
  notes = [],
  baseConfig = {},
  onFilled,
  testIdPrefix = "shortfall",
}) {
  const pct = requested > 0 ? Math.round((count / requested) * 100) : 0;
  const gap = Math.max(0, requested - count);
  const [filling, setFilling] = useState(false);
  const [progress, setProgress] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [howOpen, setHowOpen] = useState(false);

  const fillGap = async () => {
    if (filling || gap === 0) return;
    setFilling(true);
    setProgress({ phase_label: "Preparing cascade…", current: 0, total: gap });
    setElapsedSec(0);
    const t0 = Date.now();
    const clock = setInterval(() => {
      setElapsedSec(Math.round((Date.now() - t0) / 1000));
    }, 1000);

    // Sprint 60.140 — /optimize/fill-gap is now async. POST returns 202
    // immediately with `{status: "started"}`; we then poll
    // /optimize/progress until phase transitions to done / error and
    // pick the authoritative summary out of `progress.fill_gap`. This
    // eliminates the Cloudflare 100s origin-timeout that was killing
    // large fill-gaps on the first attempt (users had to retry).
    let pollTimer = null;
    const cleanup = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
      clearInterval(clock);
      setFilling(false);
      setProgress(null);
      setElapsedSec(0);
    };
    const finish = (fill) => {
      if ((fill?.added || 0) > 0) {
        toast.success(fill.message, {
          description: fill.applied_relaxations?.length
            ? `Relaxations applied: ${fill.applied_relaxations.join(" · ")}`
            : "No relaxation needed — variance jitter alone closed the gap.",
          duration: 12000,
        });
        // Sprint 60.117 — pass the fill-gap summary so the parent can
        // update `result.generated` directly. `fill.total` is
        // authoritative: existing + added.
        onFilled?.(fill);
      } else {
        toast.warning(fill?.message || "Couldn't fill the gap this time.", { duration: 10000 });
      }
      // Sprint 60.141 — Broadcast completion globally so any OTHER
      // component that shows lineup counts (Smart Optimizer page's
      // ShortfallBanner + right-rail, AppShell top-nav LU counter,
      // /My Lineups tab if not the one that triggered the fill) can
      // refetch and stop showing the stale pre-fill counts. Fixes
      // "fill worked but the Optimizer page still says 108/150".
      try {
        window.dispatchEvent(new CustomEvent("wp:fill-gap-completed", {
          detail: { added: fill?.added || 0, total: fill?.total ?? null, target: fill?.target ?? null },
        }));
      } catch { /* browsers where CustomEvent ctor is blocked */ }
      cleanup();
    };

    try {
      const kicked = await api.optimizeFillGap(requested, baseConfig);
      // Instant noop path — server had nothing to do; short-circuit.
      if (kicked?.status === "noop") {
        finish(kicked);
        return;
      }
      // Sprint 60.141 — Track OUR specific run_id so a leftover
      // phase="done" from a prior Smart Optimizer run can't
      // prematurely trip finish() before our fill-gap task has
      // actually kicked off. Backend echoes the run_id on the
      // progress doc; we only accept "done" when it matches.
      const myRunId = kicked?.run_id;
      // Poll until backend reports terminal state.
      pollTimer = setInterval(async () => {
        try {
          const p = await api.optimizeProgress();
          if (!p) return;
          // Ignore any poll whose run_id doesn't match — that's a
          // stale doc from before our POST landed. Keep polling.
          if (myRunId && p.run_id && p.run_id !== myRunId) return;
          if (p.phase && p.phase !== "idle") setProgress(p);
          if (p.phase === "done") {
            // Extra guard: fill_gap payload must be present. It's
            // only written by _run_fill_gap_task on completion, so
            // its absence means we're looking at the wrong doc.
            if (!p.fill_gap && myRunId && p.run_id !== myRunId) return;
            const fill = p.fill_gap || { added: 0, message: "Fill-gap finished." };
            finish(fill);
          } else if (p.phase === "error") {
            toast.error(
              p?.error?.detail?.message || p?.phase_label ||
              "Fill-gap failed. Try re-tuning manually."
            );
            cleanup();
          }
        } catch (_) { /* poll again next tick */ }
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fill-gap failed. Try re-tuning manually.");
      cleanup();
    }
  };

  const progressPct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.current / progress.total) * 100))
    : 0;

  return (
    <section
      data-testid={`${testIdPrefix}-banner`}
      className="border-2 border-[#FFCC00] bg-gradient-to-br from-[#1a1400] via-[#0d0d0d] to-[#0a0a0a] relative overflow-hidden"
      style={{ boxShadow: "0 0 32px rgba(255,204,0,0.12)" }}
    >
      <div className="absolute inset-0 pointer-events-none opacity-40"
           style={{ background: "radial-gradient(600px 120px at 15% 0%, rgba(255,204,0,0.18), transparent 70%)" }} />
      <div className="relative px-5 py-4">
        <div className="flex items-start gap-3">
          <Warning size={22} weight="fill" className="text-[#FFCC00] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="heading font-bold text-white text-sm uppercase tracking-widest">
                Portfolio shortfall
              </div>
              <span className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] border border-[#FFCC00]/50 px-1.5 py-0.5">
                {count}/{requested} · {pct}%
              </span>
            </div>
            <div className="text-sm text-zinc-300 mt-2 leading-relaxed">
              You requested <span className="text-white font-semibold">{requested}</span> lineups
              but the solver only found <span className="text-[#FFCC00] font-semibold">{count}</span> feasible
              combinations under your current Player Pool constraints + ownership window.
            </div>

            {/* Fill-Gap cascade explainer. Collapsed by default so the
                banner stays compact for confident users who already know
                how it works; open by clicking. */}
            <button
              type="button"
              data-testid={`${testIdPrefix}-how-toggle`}
              onClick={() => setHowOpen((v) => !v)}
              className="mt-3 flex items-center gap-1.5 text-[10px] mono uppercase tracking-widest text-[#FFCC00]/80 hover:text-[#FFCC00] transition-colors"
            >
              {howOpen ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
              How Fill-the-Gap works
            </button>
            {howOpen && (
              <div
                data-testid={`${testIdPrefix}-how-body`}
                className="mt-2 border border-[#FFCC00]/25 bg-black/50 px-3 py-2.5 space-y-2"
              >
                <div className="text-[11px] text-zinc-300 leading-relaxed">
                  <span className="text-[#39FF14] font-semibold">Your existing {count} lineups stay untouched.</span>{" "}
                  We only try to <span className="text-white font-semibold">build the missing {gap}</span> by walking
                  a cautious cascade — <span className="text-white font-semibold">one small rule relaxed at a time</span>,
                  always attempting the tightest version first. As soon as the shortfall closes, the cascade halts —
                  we never loosen more than necessary. Stack rules, Vegas QB rules, DK Two-Touch, and
                  Peer Swap stay <span className="text-white font-semibold">in effect</span> throughout.
                </div>
                <ol className="space-y-1.5 text-[11px] text-zinc-400 leading-relaxed">
                  {CASCADE_STEPS.map((s, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mono text-[9px] font-black text-[#FFCC00] bg-[#FFCC00]/10 border border-[#FFCC00]/40 px-1 py-0.5 min-w-[22px] text-center flex-shrink-0">
                        L{i + 1}
                      </span>
                      <div className="min-w-0">
                        <div className="text-zinc-200 font-semibold">{s.title}</div>
                        <div className="text-zinc-500 text-[10px] leading-snug mt-0.5">{s.detail}</div>
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="text-[10px] mono uppercase tracking-widest text-zinc-500 pt-1 border-t border-zinc-800">
                  Stop-at-first-success cascade · every rule you haven't hit stays intact
                </div>
              </div>
            )}

            {notes.length > 0 && (
              <ul className="mt-3 space-y-1.5 text-xs text-zinc-400 leading-relaxed">
                {notes.map((n) => (
                  <li key={n} className="flex items-start gap-2">
                    <span className="text-[#FFCC00] mt-0.5">→</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
            )}

            {filling && (
              <div
                data-testid={`${testIdPrefix}-fill-progress`}
                className="mt-3 border border-[#39FF14]/50 bg-black/60 px-3 py-2.5"
              >
                <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <Sparkle size={12} weight="fill" className="text-[#39FF14] animate-spin flex-shrink-0" />
                    <span className="mono text-[10px] uppercase tracking-widest text-[#39FF14] font-bold truncate">
                      {progress?.phase_label || "Cascading loosening…"}
                    </span>
                  </div>
                  <span className="mono text-[10px] uppercase tracking-widest text-zinc-400 flex-shrink-0">
                    {elapsedSec}s elapsed
                  </span>
                </div>
                <div className="relative h-1.5 bg-zinc-900 overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#39FF14] to-[#2ee30d] transition-all duration-500"
                    style={{ width: `${progressPct}%`, boxShadow: "0 0 8px rgba(57,255,20,0.6)" }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1.5 text-[9px] mono uppercase tracking-widest text-zinc-500 flex-wrap gap-2">
                  <span>
                    {progress?.current || 0}/{progress?.total || gap} built · {progressPct}%
                  </span>
                  <span className="text-zinc-600">
                    Existing {count} lineups locked · only appending new
                  </span>
                </div>
              </div>
            )}

            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <button
                type="button"
                data-testid={`${testIdPrefix}-fill-gap`}
                onClick={fillGap}
                disabled={filling || gap === 0}
                title={`Cautiously close the ${gap}-lineup gap by loosening ONE constraint at a time (salary floor → ownership window → anti-chalk). Existing lineups stay untouched; only the missing lineups are added.`}
                className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#2ee30d] disabled:opacity-50 disabled:cursor-not-allowed text-black font-black uppercase tracking-widest text-[10px] px-3 py-2 transition-colors"
              >
                <Sparkle size={11} weight="fill" className={filling ? "animate-spin" : ""} />
                {filling ? "Filling gap…" : `Fill the gap · +${gap}`}
              </button>
              <button
                type="button"
                data-testid={`${testIdPrefix}-goto-optimizer`}
                onClick={() => window.dispatchEvent(new CustomEvent("wp:goto-tab", { detail: { tab: "optimizer" } }))}
                className="inline-flex items-center gap-2 bg-transparent border border-[#FFCC00] hover:bg-[#FFCC00] hover:text-black text-[#FFCC00] font-bold uppercase tracking-widest text-[10px] px-3 py-2 transition-colors"
              >
                <Sparkle size={11} weight="fill" />
                Re-tune manually
              </button>
              <span className="text-[10px] mono uppercase tracking-widest text-zinc-500">
                Fill-gap preserves your existing lineups verbatim
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
